import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useCurrentProject } from '@/shared/hooks/useCurrentProject';
import { BaseLayout } from '@/shared/components/layout';
// Importing for the side effects: ``registerTour`` runs at module load
// for the detail and combined tour variants. We also need the combined
// tour registered so its ``hasSeen`` lookup works correctly here.
import { PUNCH_LISTS_DETAIL_TOUR_ID } from '@/features/tours/punch-lists/detail';
import { PUNCH_LISTS_COMBINED_TOUR_ID } from '@/features/tours/punch-lists/combined';
import { useTour } from '@/features/tours';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/shared/ui/accordion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/shared/ui/select';
import {
  ArrowLeft,
  Paperclip,
  Trash2,
  Upload,
  Lock,
  MoreHorizontal,
  Crosshair,
  DollarSign,
  Users,
  FileText,
  Calendar,
  User,
  CheckCircle,
  AlertCircle,
  Circle,
  Clock,
  XCircle,
  Printer,
  Camera,
  Workflow,
} from 'lucide-react';
import { usePunchListItem, usePunchListMutations, usePunchWorkflowRun } from '../hooks';
import { getPunchListReportImages } from '../api/api';
import { useToast } from '@/shared/hooks/use-toast';
import { handleApiError } from '@/shared/api/requests';
import {
  STATUS_LABELS,
  PRIORITY_LABELS,
  IMPACT_LABELS,
  type PunchListAttachment,
  type PunchListAttachmentKind,
  type PunchListReportImage,
  type PunchListStatus,
} from '../types';
import {
  AttachmentPhotoGallery,
  isLikelyImageAttachment,
} from '@/shared/components/AttachmentPhotoGallery';
import { useAllUserProjects } from '@/shared/hooks/useUserProjects';
import HotspotPanoramaDialog from '@/features/interactive-viewer-v2/panorama/HotspotPanoramaDialog';

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const STATUS_BADGE: Record<PunchListStatus, string> = {
  OPEN: 'bg-blue-100 text-blue-800',
  PENDING_APPROVAL: 'bg-amber-100 text-amber-800',
  COMPLETED: 'bg-green-100 text-green-800',
  OVERDUE: 'bg-red-100 text-red-800',
  CLOSED: 'bg-gray-100 text-gray-700',
};

const PRIORITY_BADGE: Record<string, string> = {
  HIGH: 'bg-red-100 text-red-800',
  MEDIUM: 'bg-yellow-100 text-yellow-800',
  LOW: 'bg-green-100 text-green-800',
};

const WORKFLOW_ITEM_STATUS_BADGE: Record<PunchListStatus, string> = {
  OPEN: 'bg-primary/15 text-primary hover:bg-primary/15',
  PENDING_APPROVAL: 'bg-primary/15 text-primary hover:bg-primary/15',
  COMPLETED: 'bg-success/15 text-success hover:bg-success/15',
  OVERDUE: 'bg-destructive/15 text-destructive hover:bg-destructive/15',
  CLOSED: 'bg-success/15 text-success hover:bg-success/15',
};

const WORKFLOW_TERMINAL_STATUSES = new Set<PunchListStatus>(['COMPLETED', 'CLOSED']);

interface WorkflowPhotoAreasProps {
  itemId: string;
  itemTitle: string;
  attachments: PunchListAttachment[];
  isUploading: boolean;
  onAddPhoto: (
    itemId: string,
    kind: Extract<PunchListAttachmentKind, 'before' | 'after'>,
  ) => void;
}

export const WorkflowPhotoAreas = ({
  itemId,
  itemTitle,
  attachments,
  isUploading,
  onAddPhoto,
}: WorkflowPhotoAreasProps) => {
  const photoGroups = [
    {
      label: 'Before',
      kind: 'before' as const,
      photos: attachments.filter((attachment) => attachment.kind === 'before'),
    },
    {
      label: 'After',
      kind: 'after' as const,
      photos: attachments.filter(
        (attachment) => attachment.kind === 'after' || attachment.kind === 'proof',
      ),
    },
  ];

  return (
    <div className="grid gap-3 border-t border-border bg-card/60 p-3 sm:grid-cols-2">
      {photoGroups.map(({ label, kind, photos }) => (
        <div key={kind} className="min-w-0 rounded-[11px] border border-border bg-card p-3">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {label}
          </p>
          {photos.length > 0 ? (
            <AttachmentPhotoGallery attachments={photos} maxVisible={3} label={null} />
          ) : (
            <Button
              type="button"
              variant="outline"
              className="h-auto min-h-20 w-full aspect-[3/1] rounded-lg border-dashed bg-muted/40 text-sm font-semibold text-primary hover:border-primary/50 hover:bg-primary/10 hover:text-primary"
              aria-label={`Add ${kind} photo to ${itemTitle}`}
              disabled={isUploading}
              onClick={() => onAddPhoto(itemId, kind)}
            >
              + Add Photo
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};

const STATUS_BANNER: Record<
  PunchListStatus,
  { wrap: string; icon: string; text: string; Icon: typeof CheckCircle }
> = {
  OPEN: {
    wrap: 'border-blue-200 bg-blue-50',
    icon: 'text-blue-600',
    text: 'text-blue-800',
    Icon: Circle,
  },
  PENDING_APPROVAL: {
    wrap: 'border-amber-200 bg-amber-50',
    icon: 'text-amber-600',
    text: 'text-amber-800',
    Icon: Clock,
  },
  COMPLETED: {
    wrap: 'border-green-200 bg-green-50',
    icon: 'text-green-600',
    text: 'text-green-800',
    Icon: CheckCircle,
  },
  OVERDUE: {
    wrap: 'border-red-200 bg-red-50',
    icon: 'text-red-600',
    text: 'text-red-800',
    Icon: AlertCircle,
  },
  CLOSED: {
    wrap: 'border-gray-200 bg-gray-50',
    icon: 'text-gray-600',
    text: 'text-gray-800',
    Icon: CheckCircle,
  },
};

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateTime = (iso: string | null | undefined) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const userFullName = (u: { first_name: string; last_name: string } | null | undefined) => {
  if (!u) return '—';
  const name = `${u.first_name} ${u.last_name}`.trim();
  return name || '—';
};

export const isPunchListStatusDropdownDisabled = (
  canUpdateStatus: boolean,
  isUpdating: boolean
) => !canUpdateStatus || isUpdating;

export const buildPunchListPrintAttachmentsHtml = (
  attachments: PunchListAttachment[],
  reportImages: PunchListReportImage[]
) => {
  if (attachments.length === 0) return '';

  const imageAttachmentIds = new Set(reportImages.map((image) => image.attachment_id));
  const otherAttachments = attachments.filter(
    (attachment) => !imageAttachmentIds.has(attachment.id)
  );
  const renderImageGroup = (label: string, images: PunchListReportImage[]) => images.length
    ? `<div class="photo-group"><h3>${label} (${images.length})</h3><div class="attachment-grid">${images
        .map(
          (
            image
          ) => `<a class="attachment-image" href="${escapeHtml(image.url)}" target="_blank" rel="noopener noreferrer" title="Open ${escapeHtml(image.file_name)}">
            <img src="${escapeHtml(image.url)}" alt="${escapeHtml(image.file_name)}" />
            <span>${escapeHtml(image.file_name)}</span>
          </a>`
        )
        .join('')}</div></div>`
    : '';
  const beforeImages = reportImages.filter((image) => image.kind === 'before');
  const afterImages = reportImages.filter(
    (image) => image.kind === 'after' || image.kind === 'proof'
  );
  const otherImages = reportImages.filter((image) => image.kind === 'general');
  const imageGalleryHtml = [
    renderImageGroup('Before photos', beforeImages),
    renderImageGroup('After photos', afterImages),
    renderImageGroup('Other photos', otherImages),
  ].join('');
  const otherAttachmentsHtml = otherAttachments.length
    ? `<div class="other-attachments">
        <h3>${reportImages.length ? 'Other files' : 'Files'}</h3>
        <ul>${otherAttachments
          .map(
            (attachment) =>
              `<li>${escapeHtml(attachment.file.split('/').pop() ?? attachment.file)}</li>`
          )
          .join('')}</ul>
      </div>`
    : '';

  return `<div class="section attachments-section">
    <h2>Photos &amp; Files (${attachments.length})</h2>
    ${imageGalleryHtml}
    ${otherAttachmentsHtml}
  </div>`;
};

export const PunchListDetail: React.FC = () => {
  const { id, clientName } = useParams<{ id: string; clientName?: string }>();
  const { projectIdNumber: numericProjectId } = useCurrentProject();
  const navigate = useNavigate();
  const location = useLocation();
  const listPath = location.pathname.replace(/\/[^/]+$/, '');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const beforePhotoInputRef = useRef<HTMLInputElement>(null);
  const afterPhotoInputRef = useRef<HTMLInputElement>(null);
  const workflowPhotoInputRef = useRef<HTMLInputElement>(null);
  const workflowPhotoTargetRef = useRef<{
    itemId: string;
    kind: Extract<PunchListAttachmentKind, 'before' | 'after'>;
  } | null>(null);
  const [isDeleteItemDialogOpen, setIsDeleteItemDialogOpen] = useState(false);
  const [isHotspotDialogOpen, setIsHotspotDialogOpen] = useState(false);
  const [attachmentToDeleteId, setAttachmentToDeleteId] = useState<string | null>(null);
  const { toast } = useToast();
  const { data: item, isLoading, error } = usePunchListItem(id, numericProjectId);
  const {
    data: workflowRun,
    isLoading: workflowRunLoading,
    error: workflowRunError,
  } = usePunchWorkflowRun(numericProjectId, item?.workflow_run ?? undefined);
  const { updateItem, deleteItem, approveItem, rejectItem, uploadAttachment, deleteAttachment } =
    usePunchListMutations(numericProjectId);
  const { allProjects } = useAllUserProjects(clientName);
  const projectName = allProjects.find((p) => p.id === numericProjectId)?.name ?? '';

  // Auto-start the detail-view guided tour once the item has loaded.
  //
  // Skipped when:
  //   - a tour is already active (we got here via the combined tour
  //     navigating from list → detail, and the combined tour is still
  //     in flight — let it continue uninterrupted).
  //   - the user has already seen the combined tour, which already
  //     covers the same detail content.
  const { hydrated: tourHydrated, active, startTour, hasSeen } = useTour();
  useEffect(() => {
    if (!tourHydrated) return;
    if (active) return;
    if (!item) return;
    if (hasSeen(PUNCH_LISTS_COMBINED_TOUR_ID)) return;
    void startTour(PUNCH_LISTS_DETAIL_TOUR_ID);
  }, [tourHydrated, active, startTour, hasSeen, item]);

  const handleStatusChange = (status: PunchListStatus) => {
    if (!id || !item?.can_update_status) return;
    updateItem.mutate({ id, data: { status } });
  };

  const handleApprove = async () => {
    if (!id) return;
    try {
      await approveItem.mutateAsync(id);
      toast({ title: 'Punch item approved', description: 'The item has been closed.' });
    } catch (err) {
      toast({
        title: 'Unable to approve',
        description: handleApiError(err),
        variant: 'destructive',
      });
    }
  };

  const handleReject = async () => {
    if (!id) return;
    try {
      await rejectItem.mutateAsync(id);
      toast({ title: 'Sent back', description: 'The item was reopened for rework.' });
    } catch (err) {
      toast({
        title: 'Unable to reject',
        description: handleApiError(err),
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    await deleteItem.mutateAsync(id);
    navigate(listPath);
  };

  const handleViewHotspot = () => {
    if (!item?.hotspot_detail) return;
    setIsHotspotDialogOpen(true);
  };

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    kind: PunchListAttachmentKind,
  ) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    try {
      await uploadAttachment.mutateAsync({ itemId: id, file, kind });
      toast({
        title: kind === 'before'
          ? 'Before photo added'
          : kind === 'after'
            ? 'After photo added'
            : 'File added',
      });
    } catch (err) {
      toast({
        title: 'Unable to upload',
        description: handleApiError(err),
        variant: 'destructive',
      });
    } finally {
      e.target.value = '';
    }
  };

  const handleDeleteAttachment = (attachmentId: string) => {
    setAttachmentToDeleteId(attachmentId);
  };

  const requestWorkflowPhoto = (
    itemId: string,
    kind: Extract<PunchListAttachmentKind, 'before' | 'after'>,
  ) => {
    workflowPhotoTargetRef.current = { itemId, kind };
    workflowPhotoInputRef.current?.click();
  };

  const handleWorkflowPhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const target = workflowPhotoTargetRef.current;
    if (!file || !target) return;

    try {
      await uploadAttachment.mutateAsync({ ...target, file });
      toast({ title: `${target.kind === 'before' ? 'Before' : 'After'} photo added` });
    } catch (err) {
      toast({
        title: 'Unable to upload',
        description: handleApiError(err),
        variant: 'destructive',
      });
    } finally {
      event.target.value = '';
      workflowPhotoTargetRef.current = null;
    }
  };

  const confirmDeleteAttachment = () => {
    if (!id || !attachmentToDeleteId) return;
    deleteAttachment.mutate(
      { attachmentId: attachmentToDeleteId, itemId: id },
      { onSuccess: () => setAttachmentToDeleteId(null) }
    );
  };

  const handlePrint = async () => {
    if (!item || !id || !numericProjectId) return;
    const attachments = item.attachments ?? [];
    const origin = window.location.origin;
    const printWindow = window.open('', '_blank', 'width=900,height=1200');
    if (!printWindow) return;

    printWindow.document.open();
    printWindow.document.write(`<!doctype html>
<html>
<head><meta charset="utf-8" /><title>Preparing punch list item…</title></head>
<body style="font-family:Arial,sans-serif;padding:32px;color:#0f172a;">Preparing attachment images…</body>
</html>`);
    printWindow.document.close();

    let reportImages: PunchListReportImage[] = [];
    try {
      if (attachments.length > 0) {
        reportImages = await getPunchListReportImages(numericProjectId, [id]);
      }
    } catch (err) {
      printWindow.document.open();
      printWindow.document.write(`<!doctype html>
<html>
<head><meta charset="utf-8" /><title>Unable to prepare PDF</title></head>
<body style="font-family:Arial,sans-serif;padding:32px;color:#0f172a;">
  <h1 style="font-size:20px;">Unable to load attachment images</h1>
  <p>Please close this window and try printing the item again.</p>
</body>
</html>`);
      printWindow.document.close();
      toast({
        title: 'Unable to prepare PDF',
        description: handleApiError(err),
        variant: 'destructive',
      });
      return;
    }

    const fields: [string, string][] = [
      ['Status', STATUS_LABELS[item.status]],
      ['Priority', PRIORITY_LABELS[item.priority]],
      ['Due Date', formatDate(item.due_date)],
      ['Assignee', userFullName(item.assignee)],
      ['Type', item.item_type?.name ?? '—'],
      ['Location', item.location?.name ?? '—'],
      ['Trade', item.trade?.name ?? '—'],
      ['Responsible Trade Partner', item.responsible_trade_partner?.company_name ?? '—'],
      ['Cost Code', item.cost_code ? `${item.cost_code.code} – ${item.cost_code.name}` : '—'],
      ['Cost Impact', IMPACT_LABELS[item.cost_impact]],
      ['Schedule Impact', IMPACT_LABELS[item.schedule_impact]],
      ['Punch Manager', userFullName(item.punch_manager)],
      ['Final Approver', userFullName(item.final_approver)],
      ['Created By', userFullName(item.created_by)],
      ['Created At', formatDateTime(item.created_at)],
      ['Updated At', formatDateTime(item.updated_at)],
    ];

    const metaHtml = fields
      .map(
        ([label, value]) =>
          `<div><span class="label">${escapeHtml(label)}:</span> ${escapeHtml(value)}</div>`
      )
      .join('');

    const distributionHtml = item.distribution_list.length
      ? `<div class="section"><h2>Distribution List</h2><p>${escapeHtml(
          item.distribution_list.map((u) => `${u.first_name} ${u.last_name}`.trim()).join(', ')
        )}</p></div>`
      : '';

    const attachmentsHtml = buildPunchListPrintAttachmentsHtml(attachments, reportImages);

    const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(item.title)}</title>
<style>
  @page { size: auto; margin: 0.6in; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #111827; margin: 0; }
  .header { position: relative; text-align: center; border-bottom: 2px solid #111827; padding: 52px 0 12px; margin-bottom: 18px; }
  .header .logo { position: absolute; top: 0; right: 0; height: 40px; width: auto; }
  .header .project { font-size: 13px; font-weight: 600; color: #374151; margin: 0 0 2px; }
  .header h1 { font-size: 24px; font-weight: 700; margin: 0 0 4px; }
  .header .subtitle { font-size: 12px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: #6b7280; }
  .meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 24px; font-size: 12px; margin-bottom: 20px; }
  .meta .label { color: #6b7280; font-weight: 600; }
  .section { border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px 14px; margin-bottom: 12px; break-inside: avoid; page-break-inside: avoid; }
  .section h2 { font-size: 14px; font-weight: 700; margin: 0 0 8px; }
  .section p { font-size: 12px; margin: 0; white-space: pre-wrap; }
  .section ul { margin: 0; padding-left: 18px; }
  .section li { font-size: 12px; }
  .attachments-section { break-inside: auto; page-break-inside: auto; }
  .attachment-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
  .attachment-image { display: block; color: #1d4ed8; font-size: 10px; font-weight: 600; line-height: 1.25; text-decoration: none; break-inside: avoid; page-break-inside: avoid; }
  .attachment-image img { display: block; width: 100%; height: 110px; object-fit: cover; border: 1px solid #cbd5e1; border-radius: 8px; margin-bottom: 4px; background: #f8fafc; }
  .attachment-image span { display: block; overflow-wrap: anywhere; }
  .photo-group + .photo-group { margin-top: 12px; }
  .photo-group h3, .other-attachments h3 { margin: 0 0 7px; color: #475569; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; }
  .other-attachments { margin-top: 12px; padding-top: 10px; border-top: 1px solid #e5e7eb; }
</style>
</head>
<body>
  <div class="header">
    <img class="logo" src="${origin}/static/images/iv360-p-300.webp" alt="Ironview 360" />
    ${projectName ? `<div class="project">${escapeHtml(projectName)}</div>` : ''}
    <h1>${escapeHtml(item.title)}</h1>
    <div class="subtitle">Punch List Item &middot; ${escapeHtml(STATUS_LABELS[item.status])}</div>
  </div>
  <div class="meta">${metaHtml}</div>
  <div class="section"><h2>Description</h2><p>${escapeHtml(item.description || 'No description provided.')}</p></div>
  ${distributionHtml}
  ${attachmentsHtml}
  <script>
    window.onload = function () { window.focus(); window.print(); };
    window.onafterprint = function () { window.close(); };
  </script>
</body>
</html>`;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  if (isLoading) {
    return (
      <BaseLayout>
        <div className="mx-auto w-full min-w-0 max-w-6xl space-y-4 p-4 sm:p-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </div>
          <Skeleton className="h-48 w-full" />
        </div>
      </BaseLayout>
    );
  }

  if (error || !item) {
    return (
      <BaseLayout>
        <div className="mx-auto w-full min-w-0 max-w-6xl p-4 sm:p-6">
          <Button variant="ghost" onClick={() => navigate(listPath)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <p className="mt-4 text-destructive">Failed to load punch list item.</p>
        </div>
      </BaseLayout>
    );
  }

  const attachments = item.attachments ?? [];
  const beforePhotos = attachments.filter((att) => att.kind === 'before');
  const afterPhotos = attachments.filter(
    (att) => att.kind === 'after' || att.kind === 'proof'
  );
  const otherPhotos = attachments.filter(
    (att) => att.kind === 'general' && isLikelyImageAttachment(att.file_url ?? att.file)
  );
  const nonImageAttachments = attachments.filter(
    (att) => !isLikelyImageAttachment(att.file_url ?? att.file)
  );
  const workflowItemsByStep = new Map(
    (workflowRun?.items ?? [])
      .filter((workflowItem) => workflowItem.workflow_step_key)
      .map((workflowItem) => [workflowItem.workflow_step_key, workflowItem]),
  );
  const workflowEntries = (workflowRun?.definition ?? []).map((step) => ({
    step,
    workflowItem: workflowItemsByStep.get(step.step_key),
  }));
  const completedWorkflowEntries = workflowEntries.filter(
    ({ workflowItem }) => workflowItem && WORKFLOW_TERMINAL_STATUSES.has(workflowItem.status),
  );
  const openWorkflowEntries = workflowEntries.filter(
    ({ workflowItem }) => workflowItem && !WORKFLOW_TERMINAL_STATUSES.has(workflowItem.status),
  );
  const queuedWorkflowEntries = workflowEntries.filter(
    ({ workflowItem }) => !workflowItem,
  );

  const banner = STATUS_BANNER[item.status];
  const BannerIcon = banner.Icon;
  const completedByUserName = item.completed_by ? userFullName(item.completed_by) : '';
  const completionSubmitter = item.completed_by
    ? completedByUserName !== '—'
      ? completedByUserName
      : item.completed_by.email
    : item.completion_certifier_name;
  const completionSource = completionSubmitter
    ? `${completionSubmitter}${item.submitted_company ? ` from ${item.submitted_company.name}` : ''}`
    : (item.submitted_company?.name ?? 'the QR Punch Walk');
  const bannerMessage =
    item.status === 'PENDING_APPROVAL'
      ? `Submitted for approval${item.completed_at ? ` on ${formatDate(item.completed_at)}` : ''}${
          completionSource ? ` by ${completionSource}` : ''
        }`
      : item.status === 'COMPLETED'
        ? `Completed on ${formatDate(item.updated_at)}`
        : item.status === 'CLOSED'
          ? `Closed on ${formatDate(item.updated_at)}`
          : item.status === 'OVERDUE'
            ? `Overdue${item.due_date ? ` — was due ${formatDate(item.due_date)}` : ''}`
            : `Open${item.due_date ? ` — due ${formatDate(item.due_date)}` : ''}`;

  const proofAttachments = attachments.filter((att) => att.kind === 'proof');

  return (
    <BaseLayout>
      <div className="mx-auto w-full min-w-0 max-w-6xl space-y-4 p-4 sm:space-y-6 md:p-6">
        {/* ── Header ────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <Button
              variant="outline"
              size="icon"
              className="flex-shrink-0"
              onClick={() => navigate(listPath)}
              aria-label="Back to punch lists"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <div className="flex min-w-0 items-start gap-2">
                {item.is_private && (
                  <Lock className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                )}
                <h1 className="min-w-0 break-words text-2xl font-bold tracking-tight md:text-3xl lg:truncate">
                  {item.title}
                </h1>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={PRIORITY_BADGE[item.priority]}>
                  {PRIORITY_LABELS[item.priority]} Priority
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Created {formatDateTime(item.created_at)}
                  {item.created_by && ` by ${userFullName(item.created_by)}`}
                </span>
              </div>
            </div>
          </div>

          <div
            className="w-full flex-shrink-0 lg:w-auto"
            data-tour-id="punch-list-detail-header-actions"
          >
            <div className="hidden items-center gap-2 lg:flex">
              {item.hotspot_detail && (
                <Button variant="outline" size="sm" className="gap-1.5" onClick={handleViewHotspot}>
                  <Crosshair className="h-4 w-4" />
                  View Hotspot
                </Button>
              )}

              <Button variant="outline" size="sm" className="gap-1.5" onClick={handlePrint}>
                <Printer className="h-4 w-4" />
                Print PDF
              </Button>

              <Select
                value={item.status}
                onValueChange={handleStatusChange}
                disabled={isPunchListStatusDropdownDisabled(
                  item.can_update_status,
                  updateItem.isPending
                )}
              >
                <SelectTrigger
                  className="h-9 w-44 min-w-0 gap-2 overflow-hidden"
                  aria-label={
                    item.can_update_status
                      ? 'Change punch item status'
                      : 'Punch item status is read only'
                  }
                  title={
                    item.can_update_status
                      ? undefined
                      : 'You do not have permission to change this status.'
                  }
                >
                  <span className="min-w-0 flex-1 truncate text-left font-medium">
                    {STATUS_LABELS[item.status]}
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(STATUS_LABELS) as PunchListStatus[]).map((status) => (
                    <SelectItem key={status} value={status}>
                      {STATUS_LABELS[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant="destructive"
                size="sm"
                onClick={() => setIsDeleteItemDialogOpen(true)}
                disabled={!item.can_delete || deleteItem.isPending}
                aria-label="Delete punch list item"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 lg:hidden">
              <Select
                value={item.status}
                onValueChange={handleStatusChange}
                disabled={isPunchListStatusDropdownDisabled(
                  item.can_update_status,
                  updateItem.isPending
                )}
              >
                <SelectTrigger
                  className="h-10 min-w-0 w-full gap-2 overflow-hidden"
                  aria-label={
                    item.can_update_status
                      ? 'Change punch item status'
                      : 'Punch item status is read only'
                  }
                  title={
                    item.can_update_status
                      ? undefined
                      : 'You do not have permission to change this status.'
                  }
                >
                  <span className="min-w-0 flex-1 truncate text-left font-medium">
                    {STATUS_LABELS[item.status]}
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(STATUS_LABELS) as PunchListStatus[]).map((status) => (
                    <SelectItem key={status} value={status}>
                      {STATUS_LABELS[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="h-10 gap-2">
                    <MoreHorizontal className="h-4 w-4" />
                    Actions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 max-w-[calc(100vw-2rem)]">
                  {item.hotspot_detail && (
                    <DropdownMenuItem onSelect={handleViewHotspot}>
                      <Crosshair className="mr-2 h-4 w-4" />
                      View hotspot
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onSelect={handlePrint}>
                    <Printer className="mr-2 h-4 w-4" />
                    Print PDF
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => setIsDeleteItemDialogOpen(true)}
                    disabled={!item.can_delete || deleteItem.isPending}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete item
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* ── Status banner ─────────────────────────────────────────── */}
        <Card className={banner.wrap}>
          <CardContent className="p-4 sm:p-6 sm:py-4">
            <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-start gap-2 sm:items-center">
                <BannerIcon className={`h-5 w-5 flex-shrink-0 ${banner.icon}`} />
                <span className={`min-w-0 break-words text-sm font-medium ${banner.text}`}>
                  {bannerMessage}
                </span>
              </div>
              <Badge className={`flex-shrink-0 ${STATUS_BADGE[item.status]}`}>
                {STATUS_LABELS[item.status]}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* ── Approval panel (pending approval) ─────────────────────── */}
        {item.status === 'PENDING_APPROVAL' && (
          <Card className="border-amber-200">
            <CardHeader className="p-4 pb-2 sm:p-6 sm:pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-5 w-5 text-amber-600" />
                Awaiting Approval
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 px-4 pb-4 sm:px-6 sm:pb-6">
              <p className="text-sm text-muted-foreground">
                Submitted by {completionSource}
                {item.completed_at ? ` on ${formatDateTime(item.completed_at)}` : ''}. Review the
                proof below and approve to close the item, or send it back for rework.
              </p>

              {proofAttachments.length > 0 && (
                <div>
                  <p className="mb-1.5 text-xs font-medium text-muted-foreground">
                    Completion proof ({proofAttachments.length})
                  </p>
                  <AttachmentPhotoGallery attachments={proofAttachments} />
                </div>
              )}

              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                <Button
                  onClick={handleApprove}
                  disabled={approveItem.isPending || rejectItem.isPending}
                  className="w-full gap-1.5 bg-green-600 hover:bg-green-700 sm:w-auto"
                >
                  <CheckCircle className="h-4 w-4" />
                  Approve & Close
                </Button>
                <Button
                  variant="outline"
                  onClick={handleReject}
                  disabled={approveItem.isPending || rejectItem.isPending}
                  className="w-full gap-1.5 sm:w-auto"
                >
                  <XCircle className="h-4 w-4" />
                  Send Back
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Main content: 2/3 + 1/3 on desktop ───────────────────── */}
        <div className="grid min-w-0 grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-3">
          {/* Left column */}
          <div className="min-w-0 space-y-4 sm:space-y-6 lg:col-span-2">
            {/* Details */}
            <Card data-tour-id="punch-list-detail-metadata">
              <CardHeader className="p-4 pb-3 sm:p-6 sm:pb-4">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Details
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                  <div>
                    <dt className="font-medium text-muted-foreground">Priority</dt>
                    <dd className="mt-1">
                      <Badge variant="outline" className={PRIORITY_BADGE[item.priority]}>
                        {PRIORITY_LABELS[item.priority]}
                      </Badge>
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> Due Date
                    </dt>
                    <dd className="mt-0.5">{formatDate(item.due_date)}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Type</dt>
                    <dd className="mt-0.5">{item.item_type?.name ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Location</dt>
                    <dd className="mt-0.5">{item.location?.name ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground flex items-center gap-1">
                      <User className="h-3.5 w-3.5" /> Created By
                    </dt>
                    <dd className="mt-0.5">{userFullName(item.created_by)}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> Created At
                    </dt>
                    <dd className="mt-0.5">{formatDateTime(item.created_at)}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> Updated At
                    </dt>
                    <dd className="mt-0.5">{formatDateTime(item.updated_at)}</dd>
                  </div>
                </dl>
              </CardContent>
            </Card>

            {/* Description */}
            <Card data-tour-id="punch-list-detail-description">
              <CardHeader className="p-4 pb-2 sm:p-6 sm:pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Description
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
                {item.description ? (
                  <p className="break-words whitespace-pre-wrap text-sm">{item.description}</p>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No description provided.</p>
                )}
              </CardContent>
            </Card>

            {/* Workflow */}
            {item.workflow_run && (
              <Card>
                <CardHeader className="p-4 pb-3 sm:p-6 sm:pb-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Workflow className="h-5 w-5 text-primary" />
                      Workflow
                    </CardTitle>
                    {workflowRun && (
                      <Badge
                        className={workflowRun.status === 'COMPLETED'
                          ? 'bg-success/15 text-success hover:bg-success/15'
                          : 'bg-primary/15 text-primary hover:bg-primary/15'}
                      >
                        {workflowRun.status === 'COMPLETED' ? 'Completed' : 'Active'}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
                  {workflowRunLoading && <Skeleton className="h-32 w-full" />}
                  {workflowRunError && (
                    <p className="text-sm text-destructive">Unable to load workflow details.</p>
                  )}
                  {workflowRun && (
                    <div className="space-y-4">
                      <div className="flex flex-col gap-2 rounded-[13px] border border-border bg-muted px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="font-display font-bold tracking-[-0.01em]">
                            {workflowRun.workflow_name}
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            Started {formatDate(workflowRun.created_at)}
                            {workflowRun.created_by
                              ? ` by ${userFullName(workflowRun.created_by)}`
                              : ''}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2 text-xs font-semibold">
                          <span className="rounded-full border border-border bg-card px-3 py-1.5">
                            Stage {workflowRun.current_stage} of {workflowRun.total_stages}
                          </span>
                          <span className="rounded-full border border-border bg-card px-3 py-1.5">
                            {workflowRun.completed_steps} of {workflowRun.total_steps} complete
                          </span>
                        </div>
                      </div>

                      <Accordion type="multiple" defaultValue={['open']} className="space-y-2">
                        <AccordionItem value="completed" className="border-none">
                          <AccordionTrigger className="rounded-[11px] border border-border bg-muted/50 px-3 py-2.5 hover:no-underline">
                            <span className="flex flex-1 items-center justify-between gap-3 pr-3">
                              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                                Completed
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {completedWorkflowEntries.length}
                              </span>
                            </span>
                          </AccordionTrigger>
                          <AccordionContent className="px-1">
                            <div className="space-y-2.5">
                          {completedWorkflowEntries.map(({ step, workflowItem }) => {
                            if (!workflowItem) return null;

                            return (
                              <div
                                key={step.step_key}
                                className="overflow-hidden rounded-[13px] border border-success/30 bg-success/5"
                              >
                                <Link
                                  to={`${listPath}/${workflowItem.id}`}
                                  aria-label={`Open punch item ${workflowItem.title}`}
                                  className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-success/10"
                                >
                                  <span className="min-w-0">
                                    <span className="font-semibold">{workflowItem.title}</span>
                                    <span className="mt-1 block text-xs text-muted-foreground">
                                      {workflowItem.responsible_trade_partner?.company_name
                                        ?? workflowItem.trade?.name
                                        ?? `Stage ${step.stage}`}
                                    </span>
                                  </span>
                                  <Badge className={WORKFLOW_ITEM_STATUS_BADGE[workflowItem.status]}>
                                    {STATUS_LABELS[workflowItem.status]}
                                  </Badge>
                                </Link>
                                <WorkflowPhotoAreas
                                  itemId={workflowItem.id}
                                  itemTitle={workflowItem.title}
                                  attachments={workflowItem.attachments ?? []}
                                  isUploading={uploadAttachment.isPending}
                                  onAddPhoto={requestWorkflowPhoto}
                                />
                              </div>
                            );
                          })}
                              {completedWorkflowEntries.length === 0 && (
                                <p className="px-2 py-3 text-sm text-muted-foreground">
                                  No completed items.
                                </p>
                              )}
                            </div>
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="open" className="border-none">
                          <AccordionTrigger className="rounded-[11px] border border-primary/40 bg-primary/10 px-3 py-2.5 hover:bg-primary/15 hover:no-underline">
                            <span className="flex flex-1 items-center justify-between gap-3 pr-3">
                              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
                                Open
                              </span>
                              <span className="text-xs font-semibold text-primary">
                                {openWorkflowEntries.length}
                              </span>
                            </span>
                          </AccordionTrigger>
                          <AccordionContent className="px-1">
                            <div className="space-y-2">
                          {openWorkflowEntries.map(({ step, workflowItem }) => {
                            if (!workflowItem) return null;
                            const isCurrentItem = workflowItem.id === item.id;

                            return (
                              <div
                                key={step.step_key}
                                className={`overflow-hidden rounded-[11px] border ${
                                  isCurrentItem
                                    ? 'border-primary/50 bg-primary/10 ring-1 ring-primary/20'
                                    : 'border-border bg-card'
                                }`}
                              >
                                <Link
                                  to={`${listPath}/${workflowItem.id}`}
                                  aria-label={`Open punch item ${workflowItem.title}`}
                                  className="flex w-full items-start justify-between gap-3 px-3 py-3 text-left transition-colors hover:bg-primary/10"
                                >
                                  <span className="min-w-0">
                                    <span className="flex flex-wrap items-center gap-2">
                                      <span className="font-semibold">{step.title}</span>
                                      {isCurrentItem && (
                                        <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-primary">
                                          Current item
                                        </span>
                                      )}
                                    </span>
                                    {step.description && (
                                      <span className="mt-1 block text-xs text-muted-foreground">
                                        {step.description}
                                      </span>
                                    )}
                                    <span className="mt-1 block text-xs text-muted-foreground">
                                      Stage {step.stage}
                                      {workflowItem.responsible_trade_partner?.company_name
                                        ? ` · ${workflowItem.responsible_trade_partner.company_name}`
                                        : workflowItem.trade?.name
                                          ? ` · ${workflowItem.trade.name}`
                                          : ''}
                                      {workflowItem.due_date
                                        ? ` · Due ${formatDate(workflowItem.due_date)}`
                                        : ''}
                                    </span>
                                  </span>
                                  <span className="flex shrink-0 flex-col items-end gap-1.5">
                                    <Badge className={WORKFLOW_ITEM_STATUS_BADGE[workflowItem.status]}>
                                      {STATUS_LABELS[workflowItem.status]}
                                    </Badge>
                                    <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                                      {PRIORITY_LABELS[workflowItem.priority]}
                                    </span>
                                  </span>
                                </Link>
                                <WorkflowPhotoAreas
                                  itemId={workflowItem.id}
                                  itemTitle={workflowItem.title}
                                  attachments={workflowItem.attachments ?? []}
                                  isUploading={uploadAttachment.isPending}
                                  onAddPhoto={requestWorkflowPhoto}
                                />
                              </div>
                            );
                          })}
                              {openWorkflowEntries.length === 0 && (
                                <p className="px-2 py-3 text-sm text-muted-foreground">
                                  No open items.
                                </p>
                              )}
                            </div>
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="queued" className="border-none">
                          <AccordionTrigger className="rounded-[11px] border border-border bg-muted/50 px-3 py-2.5 hover:no-underline">
                            <span className="flex flex-1 items-center justify-between gap-3 pr-3">
                              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                                Queued
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {queuedWorkflowEntries.length}
                              </span>
                            </span>
                          </AccordionTrigger>
                          <AccordionContent className="px-1">
                            <div className="space-y-2">
                          {queuedWorkflowEntries.map(({ step }) => (
                            <div
                              key={step.step_key}
                              className="flex w-full items-start justify-between gap-3 rounded-[11px] border border-border bg-card px-3 py-3 text-left opacity-65"
                            >
                              <span className="min-w-0">
                                <span className="font-semibold">{step.title}</span>
                                {step.description && (
                                  <span className="mt-1 block text-xs text-muted-foreground">
                                    {step.description}
                                  </span>
                                )}
                                <span className="mt-1 block text-xs text-muted-foreground">
                                  Stage {step.stage}
                                </span>
                              </span>
                              <Badge variant="outline" className="shrink-0">
                                Queued
                              </Badge>
                            </div>
                          ))}
                              {queuedWorkflowEntries.length === 0 && (
                                <p className="px-2 py-3 text-sm text-muted-foreground">
                                  No queued items.
                                </p>
                              )}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                      <input
                        ref={workflowPhotoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleWorkflowPhotoUpload}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Before / after photos and files */}
            <Card data-tour-id="punch-list-detail-attachments">
              <CardHeader className="p-4 pb-3 sm:p-6 sm:pb-4">
                <CardTitle className="flex min-w-0 items-center gap-2 text-base">
                  <Camera className="h-5 w-5 text-primary" />
                  <span className="truncate">Before &amp; After Photos</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
                <div className="grid gap-4 md:grid-cols-2">
                  {([
                    {
                      title: 'Before photos',
                      description: 'Document the condition before work begins.',
                      photos: beforePhotos,
                      inputRef: beforePhotoInputRef,
                      kind: 'before' as const,
                    },
                    {
                      title: 'After photos',
                      description: 'Show the completed work or repair.',
                      photos: afterPhotos,
                      inputRef: afterPhotoInputRef,
                      kind: 'after' as const,
                    },
                  ]).map((group) => (
                    <section
                      key={group.kind}
                      className="rounded-[13px] border border-border bg-muted p-4"
                    >
                      <div className="mb-3 flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-display text-sm font-bold">{group.title}</h3>
                          <p className="mt-1 text-xs text-muted-foreground">{group.description}</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 shrink-0 bg-card"
                          onClick={() => group.inputRef.current?.click()}
                          disabled={uploadAttachment.isPending}
                        >
                          <Upload className="mr-1.5 h-3.5 w-3.5" />
                          Add photo
                        </Button>
                        <input
                          ref={group.inputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(event) => handleFileUpload(event, group.kind)}
                        />
                      </div>
                      {group.photos.length ? (
                        <AttachmentPhotoGallery
                          attachments={group.photos}
                          label={null}
                          onDelete={handleDeleteAttachment}
                          isDeleting={deleteAttachment.isPending}
                        />
                      ) : (
                        <div className="rounded-[10px] border border-dashed border-border bg-card/60 px-3 py-6 text-center text-xs text-muted-foreground">
                          No {group.title.toLowerCase()} yet.
                        </div>
                      )}
                    </section>
                  ))}
                </div>

                <div className="mt-4 rounded-[13px] border border-border bg-card p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="flex items-center gap-2 font-display text-sm font-bold">
                        <Paperclip className="h-4 w-4" />
                        Other photos &amp; files
                      </h3>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Plans, documents, and uncategorized photos.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 shrink-0"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadAttachment.isPending}
                    >
                      <Upload className="mr-1.5 h-3.5 w-3.5" />
                      Add file
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      onChange={(event) => handleFileUpload(event, 'general')}
                    />
                  </div>

                  {otherPhotos.length > 0 && (
                    <div className="mt-4">
                      <AttachmentPhotoGallery
                        attachments={otherPhotos}
                        label="Other photos"
                        onDelete={handleDeleteAttachment}
                        isDeleting={deleteAttachment.isPending}
                      />
                    </div>
                  )}
                  {nonImageAttachments.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-xs font-semibold text-muted-foreground">Files</p>
                      <ul className="space-y-2">
                        {nonImageAttachments.map((att) => (
                          <li
                            key={att.id}
                            className="flex min-w-0 items-center justify-between gap-2"
                          >
                            <a
                              href={att.file_url ?? att.file}
                              target="_blank"
                              rel="noreferrer"
                              className="min-w-0 truncate text-sm text-primary hover:underline"
                            >
                              {att.file.split('/').pop()}
                            </a>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 flex-shrink-0 text-destructive hover:text-destructive"
                              onClick={() => handleDeleteAttachment(att.id)}
                              disabled={deleteAttachment.isPending}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {otherPhotos.length === 0 && nonImageAttachments.length === 0 && (
                    <p className="mt-4 text-xs text-muted-foreground">No other files or photos.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right column */}
          <div className="min-w-0 space-y-4 sm:space-y-6 lg:col-span-1">
            {/* People */}
            <Card data-tour-id="punch-list-detail-people">
              <CardHeader className="p-4 pb-2 sm:p-6 sm:pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  People
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Punch Manager</p>
                    <p className="text-sm font-medium">{userFullName(item.punch_manager)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Assignee</p>
                    <p className="text-sm font-medium">{userFullName(item.assignee)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Final Approver</p>
                    <p className="text-sm font-medium">{userFullName(item.final_approver)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Created By</p>
                    <p className="text-sm font-medium">{userFullName(item.created_by)}</p>
                  </div>
                </div>

                {item.distribution_list.length > 0 && (
                  <>
                    <hr className="my-3 border-border" />
                    <div>
                      <p className="text-xs text-muted-foreground mb-1.5">Distribution List</p>
                      <div className="flex flex-wrap gap-1.5">
                        {item.distribution_list.map((u) => (
                          <Badge
                            key={u.id}
                            variant="secondary"
                            className="max-w-full text-xs font-normal"
                          >
                            <span className="truncate">
                              {u.first_name} {u.last_name}
                            </span>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Cost & Schedule */}
            <Card>
              <CardHeader className="p-4 pb-2 sm:p-6 sm:pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Cost & Schedule
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
                <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                  <div>
                    <p className="text-xs text-muted-foreground">Cost Impact</p>
                    <p className="text-sm font-medium">{IMPACT_LABELS[item.cost_impact]}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Schedule Impact</p>
                    <p className="text-sm font-medium">{IMPACT_LABELS[item.schedule_impact]}</p>
                  </div>
                  {item.trade && (
                    <div>
                      <p className="text-xs text-muted-foreground">Trade</p>
                      <p className="text-sm font-medium">{item.trade.name}</p>
                    </div>
                  )}
                  {item.responsible_trade_partner && (
                    <div>
                      <p className="text-xs text-muted-foreground">Responsible Trade Partner</p>
                      <p className="break-words text-sm font-medium">
                        {item.responsible_trade_partner.company_name}
                      </p>
                      {item.responsible_trade_partner.trade?.name && (
                        <p className="text-xs text-muted-foreground">
                          {item.responsible_trade_partner.trade.name}
                        </p>
                      )}
                    </div>
                  )}
                  {item.cost_code && (
                    <div>
                      <p className="text-xs text-muted-foreground">Cost Code</p>
                      <p className="break-words text-sm font-medium">
                        {item.cost_code.code} &ndash; {item.cost_code.name}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={isDeleteItemDialogOpen} onOpenChange={setIsDeleteItemDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete punch list item?</DialogTitle>
            <DialogDescription>
              This will permanently remove this punch list item and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteItemDialogOpen(false)}
              disabled={deleteItem.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                await handleDelete();
                setIsDeleteItemDialogOpen(false);
              }}
              disabled={deleteItem.isPending}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={attachmentToDeleteId !== null}
        onOpenChange={(open) => {
          if (!open) setAttachmentToDeleteId(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove attachment?</DialogTitle>
            <DialogDescription>
              This file will be removed from the punch list item.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAttachmentToDeleteId(null)}
              disabled={deleteAttachment.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteAttachment}
              disabled={deleteAttachment.isPending}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {item.hotspot_detail && numericProjectId && (
        <HotspotPanoramaDialog
          open={isHotspotDialogOpen}
          hotspot={item.hotspot_detail}
          projectId={String(numericProjectId)}
          onOpenChange={setIsHotspotDialogOpen}
        />
      )}
    </BaseLayout>
  );
};
